import React, { useEffect, useState } from "react";
import { Button, Modal, OverlayTrigger, Tab, Table, Tabs, Tooltip } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import {
	BiChevronDown,
	BiChevronUp,
	BiGitMerge,
	BiLink,
	BiPencil,
	BiPlus,
	BiTrash,
	BiUnlink
} from "react-icons/bi";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import StandardService from "../../../core/service/StandardService";
import ConfirmPopup from "../../../core/utilities/ConfirmPopup";
import AddTopic from "../../Common/Standards/AddTopic/AddTopic";
import CreateDataPoint from "../../Common/Standards/CreateDataPoint/CreateDataPoint";
import CreateMetric from "../../Common/Standards/CreateMetric/CreateMetric";
import "./StandardTemplate.scss";
import RiskConfigService from "../../../core/service/RiskConfigService";
import Utils from "../../../core/utilities/Utils";

function StdTemplateConfigPane({ t, esgData, onOpenDSLink, onSaveCallback, linkSavedOn }) {

	const { stdId } = useParams();
	const [searchParams] = useSearchParams();
	const stdName = searchParams.get('name') ?? '';
	const frequency = searchParams.get('freq') ?? '';
	const [activeTab, setActiveTab] = useState('ENVIRONMENTAL');
	const [topics, setTopics] = useState([]);
	const [editedTopic, setEditedTopic] = useState({ id: -1, action: '' });
	const [editedMetric, setEditedMetric] = useState({ id: -1, topicId: 0, action: '' });
	const [editedDP, setEditedDP] = useState({ id: -1, metricId: 0, action: '' });
	const [topicVisiblity, setTopicVisibility] = useState({});
	const [metricVisiblity, setMetricVisibility] = useState({});
	const [esgDatasetTopics, setESGDatasetTopics] = useState([]);
	const [esgTopicVisiblity, setESGTopicVisibility] = useState({});
	const [esgMetricVisiblity, setESGMetricVisibility] = useState({});

	useEffect(() => {
		getTopics();
	}, [activeTab]);

	useEffect(() => {
		const newTopics = JSON.parse(JSON.stringify(esgData));
		topics?.forEach(t => {
			const matchingTopicIndex = newTopics.findIndex(e => e.id === t.esgDatasetRef);
			if (matchingTopicIndex < 0) return;

			newTopics[matchingTopicIndex]?.metrics?.forEach(m => {
				if (!t.metrics) {
					t.metrics = [];
				}
				const matchingMetricIndex = t.metrics?.findIndex(tm => tm.id === m.id && tm.fromESGDataSet);
				// If the metric is already added from esg dataset , unsaved into a saved topic, then ignore.
				if (matchingMetricIndex >= 0) return;

				t.metrics.push({
					...m,
					fromESGDataSet: true
				});
			});
			newTopics.splice(matchingTopicIndex, 1);
		});
		setESGDatasetTopics(newTopics);
	}, [esgData]);

	useEffect(() => {
		if (linkSavedOn.date === 0) return;

		if (linkSavedOn.params?.paramType === 'metric') {
			getMetricsForTopic(linkSavedOn.params.topicId);
		} else if (linkSavedOn.params?.paramType === 'dataPoint'){
			getDPsForMetric(linkSavedOn.params.topicId, linkSavedOn.params.metricId);
		}
	}, [linkSavedOn]);


	const getTopics = (refreshAll = true, updatedTopicId = 0) => {
		StandardService.getTopics(stdId, true)
			.then(res => {
				if (res.status !== 200) return;

				const newTopics = res.data?.topics?.filter(t => t.categoryType === activeTab) ?? [];
				if (refreshAll) {
					setTopics(newTopics);
				} else {
					const t = JSON.parse(JSON.stringify(topics));
					if (updatedTopicId > 0) {
						const topicIndex = newTopics?.findIndex(newTopic => newTopic.id === updatedTopicId);
						const existingTopicIndex = t?.findIndex(existingTopic => existingTopic.id === updatedTopicId);
						t[existingTopicIndex] = {
							...newTopics[topicIndex],
							metrics: t[existingTopicIndex].metrics ?? []
						};
					} else {
						t.push(newTopics[newTopics.length -1]);
					}
					setTopics(t);
				}
			})
			.catch(error => { });
	};

	const getMetricsForTopic = (topicId) => {
		StandardService.getMetrics(stdId, topicId, true)
			.then(res => {
				if (res.status !== 200) return;

				const metrics = res.data.metrics?.sort((a, b) => a.sortOrder - b.sortOrder) ?? [];
				const t = JSON.parse(JSON.stringify(topics));
				t?.forEach(topic => {
					if (topic.id !== topicId) return;

					if (!topic['metrics']) {
						topic['metrics'] = metrics;
					} else {
						const metricsFromESGDataset = topic.metrics?.filter(m => m.fromESGDataSet);
						topic.metrics = metrics;
						metricsFromESGDataset?.forEach(m => {
							topic.metrics.push(m);
						});	
					}		
				});
				setTopics(t);
			})
			.catch((error) => { });
	};

	const getDPsForMetric = (topicId, metricId) => {
		StandardService.getDataPoints(metricId, true).then(res => {
			if (res.status !== 200) return;

			const dps = res.data.body.dataPoints?.sort((a, b) => a.sortOrder - b.sortOrder) ?? [];
			const t = JSON.parse(JSON.stringify(topics));
			t?.forEach(topic => {
				if (topic.id !== topicId) return;

				topic.metrics?.forEach(metric => {
					if (metric.id !== metricId) return;

					metric['dataPoints'] = dps;
				});
			});
			setTopics(t);
		}).catch(err => { });
	};

	const renderTopicModal = () => {
		if (editedTopic.id === -1 || editedTopic.action === 'DELETE') return <></>;
		return (
			<Modal show={editedTopic.id >= 0} onHide={() => setEditedTopic({ id: -1, action: '' })}
				aria-labelledby="contained-modal-title-vcenter"
				centered>
				<Modal.Header closeButton>
					< Modal.Title as="h5">Add Topic</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<AddTopic
						stdId={stdId}
						handleDiscard={() => setEditedTopic({ id: -1, action: '' })}
						editTopicId={editedTopic.id}
						handleSave={() => {
							getTopics(false, editedTopic.id);
							setEditedTopic({ id: -1, action: '' });							
						}}
						currentcategory={activeTab}
					/>
				</Modal.Body>
			</Modal>
		);
	};

	const renderMetricModal = () => {
		if (editedMetric.id === -1 || editedMetric.action === 'DELETE') return <></>;
		return (
			<Modal show={editedMetric.id >= 0} onHide={() => setEditedMetric({ id: -1, topicId: 0 })}
				aria-labelledby="contained-modal-title-vcenter"
				centered
				dialogClassName="modal-75w">
				<Modal.Header closeButton>
					< Modal.Title as="h5">
						{t(editedMetric.id > 0 ? 'EditMetric.label' : 'AddMetric.label')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className="1">
					<CreateMetric
						stdId={stdId}
						handleDiscard={() => setEditedMetric({ id: -1, topicId: 0 })}
						topicId={editedMetric.topicId}
						editMetricId={editedMetric.id}
						answerMode={false}
						handleSave={() => {
							getMetricsForTopic(editedMetric.topicId);
							setEditedMetric({ id: -1, topicId: 0 });							
						}}
					/>
				</Modal.Body>
			</Modal>
		);
	};

	const renderDPModal = () => {
		if (editedDP.id === -1 || editedDP.action === 'DELETE') return <></>;
		return (
			<Modal show={editedDP.id >= 0} onHide={() => setEditedDP({ id: -1, metricId: 0 })}
				aria-labelledby="contained-modal-title-vcenter"
				centered>
				<Modal.Header closeButton>
					< Modal.Title as="h5">Add Datapoint</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<CreateDataPoint
						handleDiscard={() => setEditedDP({ id: -1, metricId: 0 })}
						metricId={editedDP.metricId}
						editDataPointId={editedDP.id}
						handleSave={() => {
							getDPsForMetric(editedDP.topicId, editedDP.metricId);
							setEditedDP({ id: -1, metricId: 0 });							
						}}
						editData={editedDP.data}
					/>
				</Modal.Body>
			</Modal>
		);
	};

	const renderDeleteConfirmationModal = () => {
		if (editedTopic.action !== 'DELETE'
			&& editedMetric.action !== 'DELETE'
			&& editedDP.action !== 'DELETE') return;

		return (
			<ConfirmPopup
				title={t("confirmation.label")}
				open={editedTopic.id > 0 || editedMetric.id > 0 || editedDP.id > 0}
				setOpen={() => {
					setEditedTopic({ id: -1 });
					setEditedMetric({ id: -1, topicId: 0 });
					setEditedDP({ id: -1, metricId: 0 });
				}}
				onConfirm={() => deleteEntity()}>
				<div className='text-center'>{t("ConfirmDelete.label")}</div>
			</ConfirmPopup>
		);

	};

	const deleteEntity = () => {
		let req;
		if (editedTopic.id > 0) {
			req = StandardService.deleteTopic(stdId, editedTopic.id, true);
		} else if (editedMetric.id > 0) {
			req = StandardService.deleteMetric(editedMetric.id, true);
		} else if (editedDP.id > 0) {
			req = StandardService.deleteDataPoint(editedDP.id, true);
		}
		req?.then(res => {
			if (res.status !== 200 && res.status !== 204) return;

			toast.success("Success", {
				position: toast.POSITION.TOP_RIGHT,
			});
			removeTopicMetricOrDPFromStatePostDeletion();
			setEditedTopic({ id: -1 });
			setEditedMetric({ id: -1, topicId: 0 });
			setEditedDP({ id: -1, metricId: 0 });			
		})
			.catch((error) => { });
	};

	const removeTopicMetricOrDPFromStatePostDeletion = () => {
		const t = JSON.parse(JSON.stringify(topics));
		if (editedTopic.id > 0) {
			const tIndex = t.findIndex(t => t.id === editedTopic.id);
			if (tIndex < 0) return;

			t.splice(tIndex, 1);
		} else if (editedMetric.id > 0) {
			const tIndex = t.findIndex(t => t.id === editedMetric.topicId);
			if (tIndex < 0) return;

			const mIndex = t[tIndex].metrics?.findIndex(m => m.id === editedMetric.id);
			if (mIndex < 0) return;
				
			t[tIndex].metrics.splice(mIndex, 1);
		} else if (editedDP.id > 0) {
			const tIndex = t.findIndex(t => t.id === editedDP.topicId);
			if (tIndex < 0) return;

			const mIndex = t[tIndex].metrics?.findIndex(m => m.id === editedDP.metricId);
			if (mIndex < 0) return;
			
			const dpIndex = t[tIndex].metrics[mIndex]?.dataPoints?.findIndex(d => d.id === editedDP.id);
			if (dpIndex < 0) return;

			t[tIndex].metrics[mIndex].dataPoints?.splice(dpIndex, 1);
		}
		setTopics(t);
	};

	const toggleTopicVisibility = (id, fromESGDataSet) => {
		if (fromESGDataSet) {
			const t = JSON.parse(JSON.stringify(esgTopicVisiblity));
			if (t[id] === true) {
				t[id] = false;
			} else {
				t[id] = true;
			}
			setESGTopicVisibility(t);
		} else {
			const t = JSON.parse(JSON.stringify(topicVisiblity));
			if (t[id] === true) {
				t[id] = false;
			} else {
				t[id] = true;
				getMetricsForTopic(id);
			}
			setTopicVisibility(t);
		}
	};

	const toggleMetricVisibility = (topicId, metricId, fromESGDataSet) => {
		if (fromESGDataSet) {
			const m = JSON.parse(JSON.stringify(esgMetricVisiblity));
			if (m[metricId] === true) {
				m[metricId] = false;
			} else {
				m[metricId] = true;
			}
			setESGMetricVisibility(m);
		} else {
			const m = JSON.parse(JSON.stringify(metricVisiblity));
			if (m[metricId] === true) {
				m[metricId] = false;
			} else {
				m[metricId] = true;
				getDPsForMetric(topicId, metricId);
			}
			setMetricVisibility(m);
		}
	};


	const renderTopicRows = (t) => {
		return (
			t?.map((topic, tIndex) => {
				return (
					<>
						<tr id={`topicRow-${tIndex}`} key={`topicRow-${tIndex}`} className="topic-row">
							<td>
								<div className="icon_svg cursor" onClick={() => toggleTopicVisibility(topic.id, topic.fromESGDataSet)}>
									{
										topic.fromESGDataSet
											?
											<>
												{!esgTopicVisiblity[topic.id] && <BiChevronDown />}
												{esgTopicVisiblity[topic.id] === true && <BiChevronUp />}
											</>
											:
											<>
												{!topicVisiblity[topic.id] && <BiChevronDown />}
												{topicVisiblity[topic.id] === true && <BiChevronUp />}
											</>
									}
								</div>
								<h6>{topic.name}</h6>
							</td>
							<td></td>
							<td></td>
							<td className="text-end">
								{!topic.fromESGDataSet &&
									<span className="p-2 cursor"
										onClick={() => setEditedTopic({ id: topic.id, action: 'EDIT' })}><BiPencil /></span>
								}
								<span className="p-2 cursor"
									onClick={() => {
										if (topic.fromESGDataSet) {
											const d = JSON.parse(JSON.stringify(esgDatasetTopics));
											const tIndex = d.findIndex(t => t.id === topic.id);
											if (tIndex >= 0) {
												d.splice(tIndex, 1);
												setESGDatasetTopics(d);
											}
										} else {
											setEditedTopic({ id: topic.id, action: 'DELETE' });
										}
									}}><BiTrash /></span>
							</td>
						</tr>
						{
							topic.fromESGDataSet
								?
								<>
									{esgTopicVisiblity[topic.id] === true && renderMetricRows(topic, tIndex)}
								</>
								:
								<>
									{topicVisiblity[topic.id] === true && renderMetricRows(topic, tIndex)}
								</>
						}
					</>
				);
			})
		)
	};

	const renderMetricInfo = (metric) => {
		return (
			<div>
				<ul>
					<li>{categoryToString(metric.category)}</li>
					{
						(metric.unitOfMeasure || metric.relevantUoms?.length > 0) &&
						<li>
							<span className="fw-600">{metric.unitOfMeasure}</span>
							{ metric.relevantUoms?.length > 0 && <span>{relevantUOMsToString(metric.relevantUoms)}</span>}
						</li>
					}
					<li>{getDPOrQuestionCount(metric)} <BiGitMerge /></li>
				</ul>
			</div>
		);
	};

	const renderMetricRows = (topic, tIndex) => {
		return (
			<>
				{
					topic?.metrics?.map((metric, mIndex) => {
						return (
							<>
								<tr id={`metricRow-${tIndex}-${mIndex}`} key={`metricRow-${tIndex}-${mIndex}`} className="metric-row">
									<td>
										<div className="icon_svg cursor"
											onClick={() => toggleMetricVisibility(topic.id, metric.id, topic.fromESGDataSet)}>
											{
												topic.fromESGDataSet
													?
													<>
														{!esgMetricVisiblity[metric.id] && <BiChevronDown />}
														{esgMetricVisiblity[metric.id] === true && <BiChevronUp />}
													</>
													:
													<>
														{!metricVisiblity[metric.id] && <BiChevronDown />}
														{metricVisiblity[metric.id] === true && <BiChevronUp />}
													</>
											}
										</div>
										<h6>{metric.name}</h6>
										{renderMetricInfo(metric)}
									</td>
									<td>{metric.code}</td>
									<td>
										{renderDataSourceLinkOptionForMetric(topic,tIndex, metric, mIndex)}
										{
											metric.equation?.expression &&
											<OverlayTrigger placement="top"
												overlay={<Tooltip id="eqTooltip">{metric.equation.expression}</Tooltip>}>
												<i className="cursor">fx</i>
											</OverlayTrigger>
										}
									</td>
									<td className="text-end">
										{
											!topic.fromESGDataSet &&
											<span className="p-2 cursor"
												onClick={() => setEditedMetric({ id: metric.id, topicId: topic.id, action: 'EDIT' })}><BiPencil /></span>
										}
										<span className="p-2 cursor"
											onClick={() => {
												if (topic.fromESGDataSet) {
													const d = JSON.parse(JSON.stringify(esgDatasetTopics));
													const tIndex = d.findIndex(t => t.id === topic.id);
													const mIndex = d[tIndex]?.metrics?.findIndex(m => m.id === metric.id);
													if (mIndex >= 0) {
														d[tIndex].metrics.splice(mIndex, 1);
														if (d[tIndex].metrics?.length === 0) {
															d.splice(tIndex, 1);
														}
														setESGDatasetTopics(d);
													}
												} else {
													setEditedMetric({ id: metric.id, topicId: topic.id, action: 'DELETE' });
												}
											}}><BiTrash /></span>
									</td>
								</tr>
								{
									topic.fromESGDataSet
										?
										<>
											{esgMetricVisiblity[metric.id] === true && metric.category === 'QUANTITATIVE' && renderDPRows(topic, metric, tIndex, mIndex)}
										</>
										:
										<>
											{metricVisiblity[metric.id] === true && metric.category === 'QUANTITATIVE' && renderDPRows(topic, metric, tIndex, mIndex)}
										</>
								}
								{
									topic.fromESGDataSet
										?
										<>
											{esgMetricVisiblity[metric.id] === true && metric.category === 'QUALITATIVE' && renderQualMetricQuestionRows(topic, metric, tIndex, mIndex)}
										</>
										:
										<>
											{metricVisiblity[metric.id] === true && metric.category === 'QUALITATIVE' && renderQualMetricQuestionRows(topic, metric, tIndex, mIndex)}
										</>
								}
							</>
						);
					})
				}
				<tr className="metric-row">
					<td colSpan={4}>
						<span className="cursor text-primary fw-600"
							onClick={() => setEditedMetric({ id: 0, topicId: topic.id, action: 'ADD' })}><BiPlus /> Metric</span>
					</td>
				</tr>
			</>
		);
	};

	const categoryToString = (cat) => {
		switch (cat) {
			case 'QUALITATIVE':
				return 'Qualitative';
			case 'QUANTITATIVE':
			default:
				return 'Quantitative';
		}
	};

	const relevantUOMsToString = (uoms) => {
		let str = '';
		uoms?.forEach((u, index) => {
			str += u;

			if (index !== uoms.length - 1) {
				str += ', ';
			}
		})
		return str;
	};

	const renderDPRows = (topic, metric, tIndex, mIndex) => {
		return (
			<>
				{
					metric?.dataPoints?.map((dp, dpIndex) => {
						return (
							<>
								<tr id={`dpRow-${tIndex}-${mIndex}-${dpIndex}`} key={`dpRow-${tIndex}-${mIndex}-${dpIndex}`}
									className="DP-row">
									<td>
										{dp.name}
										<div>
											<ul>
												<li><b>{dp.unitOfMeasure}</b></li>
											</ul>
										</div>
									</td>
									<td>{dp.code}</td>
									<td>{renderDataSourceLinkOptionForDP(topic, tIndex, metric, mIndex, dp, dpIndex)}</td>
									<td className="text-end">
										{
											!topic.fromESGDataSet &&
											<>
												<span className="p-2 cursor"
													onClick={() => setEditedDP({ 
														id: dp.id, 
														metricId: metric.id, 
														topicId: topic.id,
														action: 'EDIT', 
														data: dp 
													})}><BiPencil /></span>
												<span className="p-2 cursor"
													onClick={() => setEditedDP({ 
														id: dp.id, 
														metricId: metric.id, 
														topicId: topic.id,
														action: 'DELETE' 
													})}><BiTrash /></span>
											</>
										}
									</td>
								</tr>
							</>
						);
					})
				}
				<tr className="DP-row">
					<td colSpan={5}>
						<div className="DP-row_in">
							<span className="cursor text-primary fw-600"
								onClick={() => setEditedDP({ 
									id: 0, 
									metricId: metric.id, 
									topicId: topic.id,
									action: 'ADD' 
								})}><BiPlus /> Data Point</span>
						</div>
					</td>
				</tr>
			</>
		);
	};

	const renderTabContent = () => {
		return (
			<>
				<div className="p-3">
					<p className="mb-2 notes">{t('TopicAddHelper.label')}</p>
				</div>
				<div className="Add_NewTopicCard">
					<Table>
						<tbody>
							{renderTopicRows(topics)}
							{renderTopicRows(esgDatasetTopics)}
						</tbody>
					</Table>
					<div className="Add_NewTopicCard_Footer">
						<div>
							<Button variant="secondary" size="sm"
								onClick={() => setEditedTopic({ id: 0, action: 'ADD' })}><BiPlus className="me-1" /> Topic</Button>
						</div>
						<div>
							<Button variant="primary" size="sm" className="px-3" onClick={() => onSave()}>{t("Save.label")}</Button>
						</div>
					</div>
				</div>
			</>
		)
	};

	const onSave = () => {
		const payload = {
			templates: []
		};
		topics?.forEach(t => {
			const metricIds = [];
			t?.metrics?.forEach(m => {
				if (!m.fromESGDataSet) return;

				metricIds.push(m.id);
			});
			if (metricIds.length === 0) return;

			payload.templates.push({
				topicId: t.esgDatasetRef > 0 ? t.esgDatasetRef : t.id,
				metricIds: metricIds
			});
		});
		esgDatasetTopics?.forEach(t => {
			const metricIds = [];
			t?.metrics?.forEach(m => {
				metricIds.push(m.id);
			});
			payload.templates.push({
				topicId: t.id,
				metricIds: metricIds
			});
		});
		StandardService.saveFromESGDataset(stdId, payload, true).then(res => {
			if (res.status !== 200 && res.status !== 201) return;

			toast.success("Success", {
				position: toast.POSITION.TOP_RIGHT,
			});
			setEditedTopic({ id: -1 });
			setEditedMetric({ id: -1, topicId: 0 });
			setEditedDP({ id: -1, metricId: 0 });
			getTopics();
			setTopicVisibility({});
			setMetricVisibility({});
			setESGDatasetTopics([]);
			onSaveCallback();
		}).catch(err => {});
	};

	const renderDataSourceLinkOptionForMetric = (topic, tIndex, metric, mIndex) => {
		if (metric.category !== 'QUANTITATIVE' || metric.dataPointCount > 0) return <></>;

		return (
			<>
				<OverlayTrigger
					placement="top"
					overlay={Utils.renderTooltip(
						metric.riskDataSource?.id > 0
							? t("Parameterlinked.label") + " " + metric.riskDataSource.name
							: t("Nodatasourcelinked.label")
					)}>
					<span className="cursor" 
						onClick={() => onOpenDSLink({ 
							topicId: topic.id, 
							metricId: metric.id,
							dataSource: metric.riskDataSource,
							paramType: 'metric'
						})}>													
						<BiLink className={metric.riskDataSource?.id > 0 ? 'text-success link_icon' : 'text-primary link_icon'}/>
					</span>
				</OverlayTrigger>		
				{
					metric.riskDataSource?.id > 0 &&
					<span className="cursor ms-1" onClick={() => unlinkDataSource(topic.id, metric.id, metric.id, 'metric')}>
						<BiUnlink className="text-danger link_icon" />
					</span>
				}
			</>
			
		);
	};

	const renderDataSourceLinkOptionForDP = (topic, tIndex, metric, mIndex, dp, dpIndex) => {
		return (
			<>
				<OverlayTrigger
					placement="top"
					overlay={Utils.renderTooltip(
						dp.riskDataSource?.id > 0
							? t("Parameterlinked.label") + " " + dp.riskDataSource.name
							: t("Nodatasourcelinked.label")
					)}>	
					<span className="cursor" 
						onClick={() => onOpenDSLink({ 
							topicId: topic.id, 
							metricId: metric.id,
							dpId: dp.id,
							dataSource: dp.riskDataSource,
							paramType: 'dataPoint'
						})}>					
						<BiLink className={dp.riskDataSource?.id > 0 ? 'text-success link_icon' : 'text-primary link_icon'}/>
					</span>
				</OverlayTrigger>
				{
					dp.riskDataSource?.id > 0 &&
					<span className="cursor ms-1" onClick={() => unlinkDataSource(topic.id, metric.id, dp.id, 'dataPoint')}>
						<BiUnlink className="text-danger link_icon" />
					</span>
				}
			</>			
		);
	};

	const unlinkDataSource = (topicId, metricId, dsId, paramType) => {
		RiskConfigService.unlinkDatasource(stdId, dsId, paramType, true).then(res => {
			if (res.status !== 200) return;
			
			toast.success("Success", {
				position: toast.POSITION.TOP_RIGHT,
			});
			if (paramType === 'metric') {
				getMetricsForTopic(topicId);
			} else {
				getDPsForMetric(topicId, metricId);
			}
		}).catch((error) => { });
	};
	
  const getDPOrQuestionCount = (m) => {
    if (m.category === 'QUALITATIVE') {
      let count = m.question ? 1 : 0;
      count += m.question?.children?.length ?? 0;
      return count;
    } else {
      return m?.dataPointCount ? m?.dataPointCount : 0;
    }
  };

	const renderQualMetricQuestionRows = (topic, metric, tIndex, mIndex) => {
		return (
			<>
				<tr id={`quesRow-${tIndex}-${mIndex}`} key={`quesRow-${tIndex}-${mIndex}`}
					className="DP-row">
					<td>{metric.question.question}</td>
					<td>{metric.question.code}</td>
					<td></td>
					<td className="text-end">
					</td>
				</tr>
				{
					metric.question?.children?.map((child, cIndex) => {
						return (
							<tr id={`quesRow-${tIndex}-${mIndex}-${cIndex}`} key={`quesRow-${tIndex}-${mIndex}-${cIndex}`}
								className="DP-row">
								<td>{child.question}</td>
								<td>{child.question.code}</td>
								<td></td>
								<td className="text-end">
								</td>
							</tr>
						);
					})
				}
				<tr className="DP-row">
						<td colSpan={5}>
								<div className="DP-row_in">
									<span className="cursor text-primary fw-600"
											onClick={() => setEditedMetric({ id: metric.id, topicId: topic.id, action: 'EDIT' })}><BiPlus /> Question</span>
								</div>
						</td>
				</tr>
			</>
		);
	};

	return (
		<div className="card NewStandard_AddCard">
			<div className="NewStandard_AddCard_head">
				{stdName} <span className="ms-2">{frequency}</span>
			</div>
			<div className="NewStandard_AddCard_Body">
				<Tabs
					id="controlled-tab-example"
					activeKey={activeTab}
					onSelect={(k) => setActiveTab(k)}
					className="StandradTopic_Tab">
					<Tab eventKey="ENVIRONMENTAL" title="Environment">
						{renderTabContent()}
					</Tab>
					<Tab eventKey="SOCIAL" title="Social">
						{renderTabContent()}
					</Tab>
					<Tab eventKey="GOVERNANCE" title="Governance" >
						{renderTabContent()}
					</Tab>
				</Tabs>
			</div>
			{renderTopicModal()}
			{renderMetricModal()}
			{renderDPModal()}
			{renderDeleteConfirmationModal()}
		</div>
	);
}
export default withTranslation()(StdTemplateConfigPane);
